import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import VideoModal from '@/components/feature/VideoModal';
import { movies } from '@/mocks/movies';
import RelatedMovies from './components/RelatedMovies';

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const movie = movies.find((m) => m.id === id);
  const [playOpen, setPlayOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  if (!movie) {
    return (
      <div className="min-h-screen flex flex-col bg-background-50">
        <Navbar />
        <div className="h-16 md:h-[72px]" />
        <main className="flex-1 flex items-center justify-center px-4 py-24">
          <div className="text-center max-w-md">
            <span className="w-20 h-20 rounded-full bg-background-200 flex items-center justify-center text-foreground-400 mx-auto mb-6">
              <i className="ri-file-unknow-line text-4xl"></i>
            </span>
            <h1 className="font-heading font-bold text-2xl text-foreground-950">内容未找到</h1>
            <p className="text-sm text-foreground-500 mt-3 leading-relaxed">
              抱歉，你访问的影视作品不存在或已被移除，请检查链接是否正确。
            </p>
            <div className="flex items-center justify-center flex-wrap gap-3 mt-8">
              <Link
                to="/"
                className="px-5 py-2.5 rounded-md bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-home-4-line mr-1.5"></i> 返回首页
              </Link>
              <Link
                to="/category"
                className="px-5 py-2.5 rounded-md bg-background-100 border border-background-300 text-foreground-800 text-sm font-semibold hover:bg-background-200 transition-colors whitespace-nowrap"
              >
                浏览影视分类
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const related = movies
    .filter((m) => m.id !== movie.id && m.genres.some((g) => movie.genres.includes(g)))
    .sort((a, b) => b.rating - a.rating)
    .slice(0, 6);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
    } catch {
      // 剪贴板不可用时静默失败，仍给出视觉反馈（演示）
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const metaItems = [
    { label: '导演', value: movie.director, icon: 'ri-clapperboard-line' },
    { label: '主演', value: movie.cast.join('、'), icon: 'ri-user-star-line' },
    { label: '上映时间', value: `${movie.year} 年`, icon: 'ri-calendar-line' },
    { label: '类型', value: movie.genres.join(' / '), icon: 'ri-price-tag-3-line' },
    { label: '时长', value: movie.duration, icon: 'ri-time-line' },
    { label: '地区', value: movie.region, icon: 'ri-earth-line' },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <Navbar />
      <div className="h-16 md:h-[72px]" />

      <main className="flex-1">
        {/* 主视觉区 */}
        <section className="relative w-full">
          <div className="relative w-full h-[280px] md:h-[420px] overflow-hidden bg-primary-950">
            <img
              src={movie.banner || movie.cover}
              alt={`${movie.title} 剧照`}
              title={`${movie.title} 高清播放演示`}
              className="absolute inset-0 w-full h-full object-cover object-top opacity-60"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-primary-950/60 to-primary-950/40"></div>
          </div>

          <div className="w-full px-4 md:px-8 -mt-32 md:-mt-40 relative">
            <Breadcrumb items={[{ label: '影视分类', to: '/category' }, { label: movie.title }]} />

            <div className="mt-6 flex flex-col md:flex-row gap-6 md:gap-8 items-start">
              <div className="w-40 md:w-56 shrink-0 rounded-lg overflow-hidden border border-background-200 bg-background-100">
                <img
                  src={movie.cover}
                  alt={`${movie.title} 海报`}
                  title={`${movie.title} 剧情介绍`}
                  className="w-full aspect-[2/3] object-cover object-top"
                />
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-3">
                  <span className="px-2.5 py-1 rounded-md text-xs font-semibold bg-primary-500 text-white">
                    {movie.category}
                  </span>
                  <span className="px-2.5 py-1 rounded-md text-xs font-medium bg-accent-100 text-accent-900">
                    {movie.year}
                  </span>
                </div>

                <h1 className="font-heading font-extrabold text-2xl md:text-4xl text-foreground-950">
                  {movie.title}
                </h1>
                <p className="mt-1 text-foreground-500 font-heading text-sm tracking-widest uppercase">
                  {movie.enTitle}
                </p>

                <div className="flex items-center flex-wrap gap-4 mt-4 text-sm text-foreground-700">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-star-fill text-accent-500 text-lg"></i>
                    <strong className="text-foreground-950 text-base">{movie.rating.toFixed(1)}</strong>
                    <span className="text-foreground-400">/ 10</span>
                  </span>
                  <span className="w-px h-4 bg-background-300"></span>
                  <span>{movie.duration}</span>
                  <span className="w-px h-4 bg-background-300"></span>
                  <span>{movie.region}</span>
                </div>

                <p className="mt-4 text-accent-700 font-medium text-sm md:text-base">{movie.tagline}</p>

                <div className="flex items-center flex-wrap gap-3 mt-6">
                  <button
                    onClick={() => setPlayOpen(true)}
                    className="flex items-center gap-2 px-6 py-3 rounded-md bg-accent-500 text-white font-semibold text-sm hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-play-fill text-lg"></i> 在线观看（演示）
                  </button>
                  <button
                    onClick={handleCopy}
                    className="flex items-center gap-2 px-5 py-3 rounded-md bg-background-100 border border-background-300 text-foreground-800 font-semibold text-sm hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className={copied ? 'ri-check-line text-primary-600' : 'ri-link'}></i>
                    {copied ? '链接已复制' : '复制链接'}
                  </button>
                  <div className="flex items-center gap-1.5">
                    <span className="w-9 h-9 rounded-md bg-secondary-100 text-secondary-900 flex items-center justify-center">
                      <i className="ri-wechat-line"></i>
                    </span>
                    <span className="w-9 h-9 rounded-md bg-secondary-100 text-secondary-900 flex items-center justify-center">
                      <i className="ri-weibo-line"></i>
                    </span>
                    <span className="w-9 h-9 rounded-md bg-secondary-100 text-secondary-900 flex items-center justify-center">
                      <i className="ri-share-forward-line"></i>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <div className="w-full px-4 md:px-8 pb-16">
          {/* 元信息区 */}
          <section className="mt-10 md:mt-14">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
              {metaItems.map((m) => (
                <div
                  key={m.label}
                  className="flex items-start gap-3 p-4 rounded-lg bg-background-100 border border-background-200/70"
                >
                  <span className="w-9 h-9 rounded-md bg-primary-100 text-primary-700 flex items-center justify-center shrink-0">
                    <i className={m.icon}></i>
                  </span>
                  <div className="min-w-0">
                    <p className="text-xs text-foreground-500">{m.label}</p>
                    <p className="text-sm font-medium text-foreground-900">{m.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* 剧情介绍 */}
          <section className="mt-10 md:mt-14">
            <div className="flex items-center gap-3 mb-5">
              <span className="w-10 h-10 rounded-md bg-accent-500 text-white flex items-center justify-center">
                <i className="ri-book-open-line text-xl"></i>
              </span>
              <div>
                <p className="text-xs text-foreground-500 font-label uppercase tracking-widest">Storyline</p>
                <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950">剧情介绍</h2>
              </div>
            </div>
            <div className="space-y-4 text-foreground-700 leading-relaxed text-sm md:text-base max-w-4xl">
              {movie.synopsis.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </section>

          {/* 观看区域 */}
          <section className="mt-10 md:mt-14">
            <div className="flex items-center gap-3 mb-5">
              <span className="w-10 h-10 rounded-md bg-primary-500 text-white flex items-center justify-center">
                <i className="ri-play-circle-line text-xl"></i>
              </span>
              <div>
                <p className="text-xs text-foreground-500 font-label uppercase tracking-widest">Watch</p>
                <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950">在线观看</h2>
              </div>
            </div>

            <div
              className="relative w-full aspect-video rounded-lg overflow-hidden bg-primary-950 border border-background-200 cursor-pointer group"
              onClick={() => setPlayOpen(true)}
            >
              <img
                src={movie.banner || movie.cover}
                alt={`${movie.title} 播放预览`}
                className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-50 transition-opacity"
              />
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
                <span className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-accent-500 group-hover:bg-accent-600 flex items-center justify-center mb-4 transition-colors">
                  <i className="ri-play-fill text-white text-3xl md:text-4xl ml-1"></i>
                </span>
                <p className="text-white font-heading font-semibold text-base md:text-lg">播放《{movie.title}》</p>
                <p className="text-background-300 text-xs md:text-sm mt-2 max-w-md">
                  此为前端演示播放窗口，不支持真实播放或资源下载，点击即可查看演示效果。
                </p>
              </div>
            </div>
          </section>

          <RelatedMovies movies={related} />
        </div>
      </main>

      <Footer />

      <VideoModal
        open={playOpen}
        title={movie.title}
        poster={movie.cover}
        onClose={() => setPlayOpen(false)}
      />
    </div>
  );
}